import { memo, useCallback, useState } from "react";
import { type InspectionState } from "@/views/inspections/components/inspections-lists/InspectionsLists.d";
import "./InspectiosListsHeader.css";
import { useTranslate } from "@/hooks/use-translate/useTranslate";
import PrimaryButton from "@/components/buttons/primary-button/PrimaryButton";
import { Icon } from "@/components/icons/Icon";
import { noEnvRoutes } from "@/assets/routes/routes";
import api from "@/lib/api";
import ProblemMessage from "@/components/problem-message/ProblemMessage";
import TopAlertMessage from "@/components/alert-message/TopAlertMessage";
import { appToast } from "@/lib/toast";
import { useLoader } from "@/context/loader-context/LoaderContext";
import SecondaryButton from "@/components/buttons/secondary-button/SecondaryButton";
import { useUserStore } from "@/store/useUser";

interface InpsectionsListsProps {
  addInspection: () => void;
  chargeTable: (status: InspectionState) => void;
  activeTable: InspectionState;
}

interface SharePointImportReport {
  inserted: number;
  updated: number;
  ignored: number;
  errors: number;
  errorMessages: string[];
}

const InspectionsListHeader = ({
  addInspection,
  chargeTable,
  activeTable,
}: InpsectionsListsProps) => {
  const translate = useTranslate();
  const { showLoader, hideLoader } = useLoader();
  const { isSupervisor } = useUserStore();
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");

  const chargeActiveInspectionsTable = useCallback(() => {
    chargeTable("active");
  }, [chargeTable]);

  const chargeClosedInspectionsTable = useCallback(() => {
    chargeTable("closed");
  }, [chargeTable]);

  const handleImportClick = () => {
    document.getElementById("csvImportInput")?.click();
  };

  const handleCsvSelected = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const csvFile = new FormData();
    csvFile.append("file", file);

    try {
      await api.post(noEnvRoutes.postImportCsvInspections, csvFile, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setSuccess(translate("importCsvSuccess"));
    } catch (error: any) {
      const errors =
        error?.response?.data?.errors?.join(", ") ||
        error?.response?.data?.message ||
        translate("error");

      setError(errors);
    }

    event.target.value = "";
  };

  const handleOnimportSharePoint = async () => {
    showLoader();
    try {
      const { data: report } = await api.post<SharePointImportReport>(
        noEnvRoutes.importSharePointInspections,
      );

      const summary = translate("importSharePointSummary", [
        report.inserted.toString(),
        report.updated.toString(),
        report.ignored.toString(),
        report.errors.toString(),
      ]);
      const details = report.errorMessages.join(". ");
      const message = details ? `${summary} ${details}` : summary;
      const hasChanges = report.inserted > 0 || report.updated > 0;

      if (report.errors === 0) {
        appToast.success(translate("success"), message);
      } else if (hasChanges) {
        appToast.warning(translate("importSharePointPartial"), message);
      } else {
        appToast.error(translate("importSharePointFailed"), message);
      }

      chargeTable(activeTable);
    } catch (error) {
      const e = error as Error;
      appToast.error(translate("error"), e.message);
    } finally {
      hideLoader();
    }
  };

  return (
    <div className="px-16 flex flex-row w-full justify-between items-center md:h-16 flex-wrap-reverse bg-[#FAFAFA] border-b">
      <div className="flex flex-row gap-x-4 h-14 md:h-full items-end max-md:w-full max-md:justify-center">
        <button
          type="button"
          className={
            activeTable === "active"
              ? "shown-inspections-tab"
              : "hidden-inspections-tab"
          }
          onClick={chargeActiveInspectionsTable}
        >
          <span>
            <Icon name="overview" className="tab-icon size-5" />
          </span>
          <span>{translate("actives")}</span>
        </button>
        <button
          type="button"
          className={
            activeTable === "closed"
              ? "shown-inspections-tab"
              : "hidden-inspections-tab"
          }
          onClick={chargeClosedInspectionsTable}
        >
          <span>
            <Icon name="archive" className="tab-icon size-5" />
          </span>

          <span>{translate("closed")}</span>
        </button>
      </div>
      {!isSupervisor ? (
        <div className="max-md:hidden">
          <PrimaryButton
            label={translate("newInspection")}
            width="fit-content"
            size="medium"
            colour="red"
            icon="circlePlus"
            onClick={addInspection}
          />
        </div>
      ) : (
        <div className="max-md:hidden flex flex-row gap-x-4">
          <div>
            <label className="hidden" htmlFor="csvImportInput">
              add-file
            </label>
            <input
              id="csvImportInput"
              type="file"
              accept=".csv"
              className="hidden"
              onChange={handleCsvSelected}
            />

            <PrimaryButton
              label={translate("importCsv")}
              width="fit-content"
              size="medium"
              colour="red"
              icon="circlePlus"
              onClick={handleImportClick}
            />
          </div>
          <div>
            <SecondaryButton
              label={translate("importSharePoint")}
              width="fit-content"
              size="medium"
              colour="red"
              icon="refresh"
              onClick={handleOnimportSharePoint}
            />
          </div>
        </div>
      )}

      {error && (
        <ProblemMessage
          titleLabel="aProblem"
          error={error}
          closeMessage={() => setError("")}
        />
      )}

      {success && (
        <TopAlertMessage
          titleLabel="success"
          textLabel={success}
          contentType="success"
          formatType="feedback"
          formattedText
          closeMessage={() => setSuccess("")}
        />
      )}
    </div>
  );
};

export default memo(InspectionsListHeader);
